import { ReactElement, useState, useEffect, useCallback } from 'react'
import {
  Table,
  Button,
  Modal,
  Input,
  Select,
  Typography,
  Tag,
  Popconfirm,
  Alert,
  Flex,
  Form,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons'
import { connect } from 'react-redux'
import {
  fetchApiKeys,
  createApiKey,
  deleteApiKey,
} from 'actions/settings/api-keys'
import {
  getApiKeys,
  isFetchingApiKeys,
  isCreatingApiKey,
} from 'selectors/settings/api-keys'
import type { ApiKey, ApiKeyRole } from 'types/settings/api-keys'
import moment from 'moment'

const { Text, Paragraph } = Typography

const ROLE_COLORS: Record<ApiKeyRole, string> = {
  FULL: 'green',
  READ_ONLY: 'blue',
  INGESTION: 'orange',
}

const ROLE_LABELS: Record<ApiKeyRole, string> = {
  FULL: 'Full Access',
  READ_ONLY: 'Read Only',
  INGESTION: 'Ingestion',
}

interface ReduxState {
  apiKeys: ApiKey[]
  isFetching: boolean
  isCreating: boolean
}

const select = (): ReduxState => ({
  apiKeys: getApiKeys(),
  isFetching: isFetchingApiKeys(),
  isCreating: isCreatingApiKey(),
})

const ApiKeys = (props: ReduxState): ReactElement => {
  const { apiKeys, isFetching, isCreating } = props

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [form] = Form.useForm()

  useEffect(() => {
    fetchApiKeys()
  }, [])

  const handleCreate = useCallback(async () => {
    const values = await form.validateFields()
    const key = await createApiKey(values.name, values.role)
    if (key) {
      setCreateModalOpen(false)
      form.resetFields()
      setCreatedKey(key)
    }
  }, [form])

  const handleCopy = useCallback(() => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [createdKey])

  const handleCloseCreated = useCallback(() => {
    setCreatedKey(null)
    setCopied(false)
  }, [])

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Key',
      dataIndex: 'keyPrefix',
      key: 'keyPrefix',
      render: (keyPrefix: string) => <Text code>{keyPrefix}</Text>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: ApiKeyRole) => (
        <Tag color={ROLE_COLORS[role]}>{ROLE_LABELS[role]}</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => moment(date).format('MMM D, YYYY'),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, record: ApiKey) => (
        <Popconfirm
          title="Delete API key"
          description="Applications using this key will lose access."
          onConfirm={() => deleteApiKey(record.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ]

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Paragraph style={{ margin: 0, color: '#666' }}>
          API keys allow external applications to authenticate with the
          VirtualLog API.
        </Paragraph>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create API Key
        </Button>
      </Flex>

      <Table
        dataSource={apiKeys}
        columns={columns}
        rowKey="id"
        loading={isFetching}
        pagination={false}
        locale={{
          emptyText:
            'No API keys yet. Create one to start using the VirtualLog API.',
        }}
      />

      {/* Create Modal */}
      <Modal
        title="Create API Key"
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModalOpen(false)
          form.resetFields()
        }}
        okText="Create"
        confirmLoading={isCreating}
      >
        <Form form={form} layout="vertical" initialValues={{ role: 'FULL' }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g. My Application" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'FULL', label: 'Full Access — Read + write logs' },
                { value: 'READ_ONLY', label: 'Read Only — Query logs only' },
                { value: 'INGESTION', label: 'Ingestion — Push logs only' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Key Created Modal */}
      <Modal
        title="API Key Created"
        open={createdKey !== null}
        onCancel={handleCloseCreated}
        footer={[
          <Button key="done" type="primary" onClick={handleCloseCreated}>
            Done
          </Button>,
        ]}
      >
        <Alert
          message="Copy this key now. You won't be able to see it again."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Flex gap={8}>
          <Input
            value={createdKey || ''}
            readOnly
            style={{ fontFamily: 'monospace' }}
          />
          <Button
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={handleCopy}
            type={copied ? 'default' : 'primary'}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Flex>
      </Modal>
    </>
  )
}

export default connect(select)(ApiKeys)
